import { jsx as _jsx } from "react/jsx-runtime";
import React, { memo, useContext, useMemo } from 'react';
import { UXPContext } from '@core/context/Context';
import ComponentRenderer from '@helper/ComponentRenderer/ComponentRenderer';
import ErrorBoundary from '@helper/ErrorBoundary/ErrorBoundary';
import { hasValue, parseComponentId } from '@utils';
import { PageEditorPreviewContext } from '@core/context/PageEditorPreviewContext';
const PluggableViewBase = ({ viewId, defaultPage, props: componentProps, isPreview = false }) => {
    var _a, _b;
    const context = useContext(UXPContext);
    const normalizedViewId = useMemo(() => viewId.toLowerCase(), [viewId]);
    const override = useMemo(() => { var _a; return (_a = context.viewOverrides) === null || _a === void 0 ? void 0 : _a[normalizedViewId]; }, [context.viewOverrides, normalizedViewId]);
    const pageIdToRender = useMemo(() => {
        if (hasValue(override === null || override === void 0 ? void 0 : override.pageId)) {
            return override.pageId.toLowerCase();
        }
        if (typeof defaultPage === 'string') {
            return defaultPage.toLowerCase();
        }
        return null;
    }, [override, defaultPage]);
    if (pageIdToRender) {
        const parsed = parseComponentId(pageIdToRender);
        if (!parsed) {
            console.error(`[PluggableView] Invalid pageId format: ${pageIdToRender} for viewId: ${normalizedViewId}`);
            return (_jsx("div", { className: "uxpcore_pluggableview__error", children: ((_a = context.$L) === null || _a === void 0 ? void 0 : _a.call(context, 'uxp-core.text.pluggable-view-not-found')) ||
                    'Unable to find the configured view' }));
        }
        const overrideConfiguredProps = override === null || override === void 0 ? void 0 : override.configuredProps;
        return (_jsx(PageEditorPreviewContext.Provider, { value: isPreview, children: _jsx("div", { className: "uxpcore_pluggableview", "data-view-id": normalizedViewId, children: _jsx(ComponentRenderer, { moduleId: parsed.moduleId, componentId: parsed.componentId, type: parsed.type, configuredProps: overrideConfiguredProps, additionalProps: componentProps, isPreview: isPreview }) }) }));
    }
    // If component reference, render directly
    if (typeof defaultPage !== 'string') {
        return (_jsx(PageEditorPreviewContext.Provider, { value: isPreview, children: _jsx("div", { className: "uxpcore_pluggableview", "data-view-id": normalizedViewId, children: React.createElement(defaultPage, Object.assign(Object.assign({}, componentProps), { isPreview })) }) }));
    }
    // Invalid configuration
    console.error(`[PluggableView] Invalid default value for viewId: ${normalizedViewId}. Must be a pageId string or React component.`);
    return (_jsx("div", { className: "uxpcore_pluggableview__error", children: ((_b = context.$L) === null || _b === void 0 ? void 0 : _b.call(context, 'uxp-core.text.pluggable-view-not-found')) ||
            'Unable to find the configured view' }));
};
/**
 * PluggableView Component
 *
 * Wraps any view/component that should be replaceable by users.
 * Automatically checks for overrides and renders the appropriate component.
 *
 * @example
 * ```typescript
 * // Using with pageId string
 * <PluggableView
 *     viewId="/Location/details/info"
 *     defaultPage="iviva-location-app/widget/InfoPanel"
 *     props={{ locationId: "123" }}
 * />
 *
 * // Using with component reference
 * <PluggableView
 *     viewId="/Location/details/tabs/settings"
 *     defaultPage={SettingsTabComponent}
 *     props={{ locationId: "123" }}
 * />
 * ```
 */
const PluggableView = memo((props) => (_jsx(ErrorBoundary, { children: _jsx(PluggableViewBase, Object.assign({}, props)) }, props === null || props === void 0 ? void 0 : props.viewId)));
export default PluggableView;
